if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.currentVariant = null;

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        this.buyNowContainer = this.querySelector('.product-form__buttons[data-buy-now]');
        this.buyNowMin = parseInt(this.buyNowContainer?.dataset.buyNowMin, 10) || 0;
        this.buyNowPrice = parseInt(this.buyNowContainer?.dataset.buyNowPrice, 10) || 0;
        this.onBuyNowQuantityChange = this.syncBuyNow.bind(this);
        this.unsubscribers = [];
      }

      connectedCallback() {
        if (!this.buyNowContainer) return;

        this.unsubscribers = [
          subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event?.data?.sectionId && event.data.sectionId !== this.dataset.sectionId) return;
            // Fail closed: a variant with no readable price never shows Buy it now.
            this.buyNowPrice = parseInt(event?.data?.variant?.price, 10) || 0;
            this.syncBuyNow();
          }),
          subscribe(PUB_SUB_EVENTS.quantityUpdate, this.onBuyNowQuantityChange),
        ];
        document.addEventListener('change', this.onBuyNowQuantityChange);
        document.addEventListener('input', this.onBuyNowQuantityChange);
        this.syncBuyNow();
      }

      disconnectedCallback() {
        this.unsubscribers.forEach((unsubscribe) => unsubscribe());
        this.unsubscribers = [];
        document.removeEventListener('change', this.onBuyNowQuantityChange);
        document.removeEventListener('input', this.onBuyNowQuantityChange);
      }

      // Buy it now goes straight to checkout, so it must only be available while the cart minimum is met.
      syncBuyNow() {
        if (!this.buyNowContainer) return;

        // getAttribute: form.id is shadowed by the form's hidden input[name="id"].
        const quantityInput = document.querySelector(`.quantity__input[form="${this.form.getAttribute('id')}"]`);
        const quantity = quantityInput ? parseInt(quantityInput.value, 10) || 0 : 1;
        const meetsMinimum = this.buyNowPrice * quantity >= this.buyNowMin;

        this.buyNowContainer.dataset.buyNow = meetsMinimum ? 'on' : 'off';
        this.submitButton.classList.toggle('button--secondary', meetsMinimum);
        this.submitButton.classList.toggle('button--primary', !meetsMinimum);
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const ctaContext = this.getCtaContext(formData);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.trackCtaAdd(ctaContext);
              window.location = window.routes.cart_url;
              return;
            }

            this.trackCtaAdd(ctaContext);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
                ctaLabel: ctaContext.cta_label,
                ctaLabelVariant: ctaContext.cta_label_variant,
                ctaContext,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      if (document.querySelector('cart-drawer')) {
                        if (this.cart) this.cart.renderContents(response, false);
                        document.dispatchEvent(new CustomEvent('product:added', { detail: { item: response } }));
                      } else {
                        if (this.cart) this.cart.renderContents(response);
                      }
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                if (document.querySelector('cart-drawer')) {
                  if (this.cart) this.cart.renderContents(response, false);
                  document.dispatchEvent(new CustomEvent('product:added', { detail: { item: response } }));
                } else {
                  if (this.cart) this.cart.renderContents(response);
                }
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text, variant) {
        if (variant !== undefined) this.currentVariant = variant;

        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = this.getSubmitLabel(variant);
        }
      }

      getSubmitLabel(variant = this.currentVariant) {
        const productSystem = this.getProductSystem();

        if (productSystem === 'kit') {
          return this.submitButton?.dataset.ctaLabelDefault || window.variantStrings.addToCart;
        }

        if (this.variantLooksFramed(variant) || this.selectedOptionsLookFramed()) {
          return this.submitButton?.dataset.ctaLabelFrame || 'Add Frame to Cart';
        }

        if (productSystem === 'single') {
          return this.submitButton?.dataset.ctaLabelPrint || 'Add Print to Cart';
        }

        return this.submitButton?.dataset.ctaLabelDefault || window.variantStrings.addToCart;
      }

      getProductSystem() {
        const productInfo = this.closest('product-info') || document;
        const pickerSystem = productInfo.querySelector('variant-selects')?.dataset.productSystem;

        return pickerSystem || this.submitButton?.dataset.productSystem || 'default';
      }

      variantLooksFramed(variant) {
        return Boolean(
          variant?.options?.some((option) => String(option || '').toLowerCase().includes('frame'))
        );
      }

      selectedOptionsLookFramed() {
        const productInfo = this.closest('product-info') || document;
        const selectedValues = [
          ...Array.from(productInfo.querySelectorAll('variant-selects select')).map(
            (select) => select.selectedOptions?.[0]?.textContent || select.value
          ),
          ...Array.from(productInfo.querySelectorAll('variant-selects input[type="radio"]:checked')).map(
            (input) => input.value
          ),
        ];

        return selectedValues.some((value) => String(value || '').toLowerCase().includes('frame'));
      }

      getSelectedOptionValues() {
        const productInfo = this.closest('product-info') || document;
        const selectedValues = [
          ...Array.from(productInfo.querySelectorAll('variant-selects select')).map(
            (select) => select.selectedOptions?.[0]?.textContent?.trim() || select.value
          ),
          ...Array.from(productInfo.querySelectorAll('variant-selects input[type="radio"]:checked')).map(
            (input) => input.value
          ),
        ];

        return selectedValues.filter(Boolean);
      }

      getCtaLabelVariant(label = this.getSubmitLabel()) {
        const productSystem = this.getProductSystem();
        const frameLabel = this.submitButton?.dataset.ctaLabelFrame || 'Add Frame to Cart';
        const printLabel = this.submitButton?.dataset.ctaLabelPrint || 'Add Print to Cart';

        if (label === frameLabel) return 'single_frame';
        if (productSystem === 'single' || label === printLabel) return 'single_print';
        if (productSystem === 'kit') return 'kit';

        return 'default';
      }

      getCtaContext(formData) {
        const label = this.getSubmitLabel();

        return {
          source: 'product-form',
          cta_label: label,
          cta_label_variant: this.getCtaLabelVariant(label),
          product_system: this.getProductSystem(),
          product_type: this.submitButton?.dataset.productType || this.getProductSystem(),
          product_id: this.submitButton?.dataset.productId || '',
          product_handle: this.submitButton?.dataset.productHandle || '',
          template_type: this.submitButton?.dataset.templateType || window.ArtTheoryAnalytics?.context?.template_type || '',
          collection_handle: this.submitButton?.dataset.collectionHandle || window.ArtTheoryAnalytics?.context?.collection_handle || '',
          variant_id: formData.get('id'),
          variant_options: this.getSelectedOptionValues(),
        };
      }

      trackCtaAdd(context) {
        try {
          if (window.ArtTheoryAnalytics && typeof window.ArtTheoryAnalytics.track === 'function') {
            window.ArtTheoryAnalytics.track('add_to_cart', context);
            return;
          }

          if (Array.isArray(window.dataLayer)) {
            window.dataLayer.push({ event: 'add_to_cart', ...context });
          }
        } catch (error) {
          console.warn('CTA analytics tracking failed', error);
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
